import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, Teleport as _Teleport, withKeys as _withKeys, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, withModifiers as _withModifiers, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  role: "search"
}
const _hoisted_2 = ["id"]
const _hoisted_3 = { class: "sr-only" }
const _hoisted_4 = { class: "tw" }
const _hoisted_5 = {
  id: "searchHeadline",
  class: "sr-only"
}
const _hoisted_6 = { class: "relative" }

import { ref, useTemplateRef, watch, nextTick, onMounted, onBeforeUnmount, onUnmounted } from "vue";
import { useBreakpoints, onClickOutside } from "@vueuse/core";
import { useI18n } from "vue-i18n";

import SvgIcon from "@SharedVueComponents/SvgIcon/SvgIcon.vue";
import SearchForm from "./components/SearchForm.vue";
import DepartmentToggle from "./components/DepartmentToggle.vue";
import CloseButton from "@SharedVueComponents/CloseButton/CloseButton.vue";
import { inertLandmarksAndSkipLinks, removeInertFromLandmarksAndSkipLinks } from "@ocm/services/services.inert";

import useSearch from "./composables/use-search";
import useScrollBehaviourBody from "./composables/use-scroll-behaviour-body";

import OcmMessenger from "ocm_mercurius_messenger";
import { messengerPublicationTypes } from "@ocm/services/services.constants";
import type { MessengerData } from "@ocm/types/OcmMessenger.type";
import { animationDurationMs } from "@ocm/services/services.modal";
import { skipLinks } from "@ocm/services/services.skipLinks";
import { tr_searchClick } from "./services/dataLayer";


export default /*@__PURE__*/_defineComponent({
  __name: 'SearchApp',
  setup(__props) {

const { t } = useI18n();

const OPEN_SEARCH_BUTTON_ID = skipLinks.search.rightDrawerButton.slice(1); // #open-search-right-drawer

const isScreen1408px = useBreakpoints({ px1408: "88em" }).greaterOrEqual("px1408");
const { userInput, isSearchVisible, openSearch, closeSearch, triggerSearch } = useSearch();
const searchAppEl = useTemplateRef("searchAppEl");
const searchDialogEl = useTemplateRef("searchDialogEl");

onClickOutside(searchAppEl, () => isScreen1408px.value && closeSearch());

const searchBoxHeight = ref("");
const _setSearchBoxHeight = () => {
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	searchBoxHeight.value = `${window.visualViewport!.height}px`;
};

const _resetSearchBoxHeight = () => {
	window.visualViewport?.removeEventListener("resize", _setSearchBoxHeight);
	searchBoxHeight.value = "";
};

const timeout = ref<number>();
onBeforeUnmount(() => {
	clearTimeout(timeout.value);
});
const isDrawerOpen = ref(false);

const openRightCanvas = () => {
	tr_searchClick("Icon");
	onOpenSearch();
};

const onOpenSearch = () => {
	// called by (this.)openRichtCanvas() and from outside using messenger: OPEN_SEARCH (left canvas)
	inertLandmarksAndSkipLinks(searchDialogEl.value);
	openSearch();
	setAriaExpanded(true);
};

const searchSubmitted = ref(false);
const _onSearchSubmitted = () => {
	searchSubmitted.value = true;
};

const onCloseSearch = () => {
	removeInertFromLandmarksAndSkipLinks();

	if (isScreen1408px.value) closeSearch();
	else {
		timeout.value = setTimeout(closeSearch, animationDurationMs);
		// if user submitted a search request, focus will be handled in ResultAmount.vue
		if (isDrawerOpen.value && !searchSubmitted.value) focusOpenSearchButton();
		isDrawerOpen.value = false;
		setAriaExpanded(false);
	}
	searchSubmitted.value = false;
};

function setAriaExpanded(value: boolean) {
	document
		.querySelector<HTMLButtonElement>(skipLinks.search.rightDrawerButton)
		?.setAttribute("aria-expanded", `${value}`);
	document
		.querySelector<HTMLButtonElement>(skipLinks.search.leftDrawerButton)
		?.setAttribute("aria-expanded", `${value}`);
}

const { lockBody, unlockBody } = useScrollBehaviourBody();
watch(isSearchVisible, async (isSearchVisible) => {
	if (isSearchVisible) {
		await nextTick();
		if (!isScreen1408px.value) {
			isDrawerOpen.value = true;
			lockBody();
		}
		window.visualViewport?.addEventListener("resize", _setSearchBoxHeight);
	} else {
		isDrawerOpen.value = false;
		unlockBody();
		_resetSearchBoxHeight();
	}
});

watch(isScreen1408px, (isScreen1408px) => {
	if (!isSearchVisible.value) return;
	isDrawerOpen.value = !isScreen1408px;
	isScreen1408px ? unlockBody() : lockBody();
});

const onTriggerSearch = ({ data }: MessengerData) => {
	if (typeof data !== "string") return;
	userInput.value = data;
	triggerSearch();
};

const handleDialogEscapeKeyDown = () => {
	if (isSearchVisible.value) {
		onCloseSearch();
	}
};

const isScreenSmallerThan1024px = useBreakpoints({ px1024: "64em" }).smaller("px1024");
const _skipLinkEl = document.querySelector<HTMLAnchorElement>("#js-skip-to-search");
const _updateSkipLink = () => {
	if (!_skipLinkEl) return;

	switch (true) {
		case isScreenSmallerThan1024px.value:
			_skipLinkEl.setAttribute("href", skipLinks.search.leftDrawerButton);
			break;
		case isScreen1408px.value:
			_skipLinkEl.setAttribute("href", skipLinks.search.input);
			break;
		default:
			_skipLinkEl.setAttribute("href", skipLinks.search.rightDrawerButton);
	}
};

onMounted(() => {
	OcmMessenger.subscribe(messengerPublicationTypes.OPEN_SEARCH, onOpenSearch);
	OcmMessenger.subscribe(messengerPublicationTypes.SEARCH_SUBMITTED, _onSearchSubmitted);
	OcmMessenger.subscribe(messengerPublicationTypes.CLOSE_SEARCH, onCloseSearch);

	if (document.querySelectorAll("[data-id=search]").length === 1) {
		OcmMessenger.subscribe(messengerPublicationTypes.TRIGGER_SEARCH, onTriggerSearch);
	}

	window.addEventListener("resize", _updateSkipLink);
	_updateSkipLink();
});

onUnmounted(() => {
	_resetSearchBoxHeight();
	window.removeEventListener("resize", _updateSkipLink);
});

function focusOpenSearchButton() {
	switch (true) {
		case isScreenSmallerThan1024px.value:
			document.querySelector<HTMLButtonElement>(skipLinks.search.leftDrawerButton)?.focus();
			break;
		case !isScreenSmallerThan1024px.value && !isScreen1408px.value:
			document.querySelector<HTMLButtonElement>(skipLinks.search.rightDrawerButton)?.focus();
			break;
	}
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_openBlock(), _createBlock(_Teleport, { to: "#header-search-container" }, [
      _createElementVNode("div", {
        ref_key: "searchAppEl",
        ref: searchAppEl,
        class: "tw",
        "data-id": "search"
      }, [
        (!_unref(isScreen1408px))
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createElementVNode("button", {
                id: _unref(OPEN_SEARCH_BUTTON_ID),
                class: "flex h-11 scroll-mt-1.5 items-center justify-center px-3",
                "aria-haspopup": "dialog",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (openRightCanvas()))
              }, [
                _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(t)("siteWide.searchApp.search.openSearch")), 1),
                _createVNode(SvgIcon, {
                  class: "size-5",
                  "icon-name": "search"
                })
              ], 8, _hoisted_2)
            ]))
          : (_openBlock(), _createBlock(SearchForm, { key: 1 }))
      ], 512)
    ])),
    (!_unref(isScreen1408px))
      ? (_openBlock(), _createBlock(_Teleport, {
          key: 0,
          to: "body"
        }, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", {
              class: _normalizeClass(["srh__drawer-overlay fixed left-0 top-0 z-[999] h-full w-screen justify-start desktop:justify-end", [_unref(isSearchVisible) ? 'flex' : 'hidden', { 'bg-black/70': isDrawerOpen.value }]]),
              style: _normalizeStyle({ '--transition-duration': `${_unref(animationDurationMs)}ms` }),
              onClick: _withModifiers(onCloseSearch, ["self","stop"])
            }, [
              _createElementVNode("div", {
                ref_key: "searchDialogEl",
                ref: searchDialogEl,
                role: "dialog",
                class: _normalizeClass(["srh__drawer flex max-h-full w-[35.7142857rem] max-w-full transform flex-col overflow-hidden overscroll-contain bg-white text-sm", { '-translate-x-full desktop:translate-x-full': !isDrawerOpen.value }]),
                style: _normalizeStyle({ height: searchBoxHeight.value }),
                "aria-modal": "true",
                "aria-labelledby": "searchHeadline",
                onKeydown: _withKeys(handleDialogEscapeKeyDown, ["escape"])
              }, [
                _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(t)("siteWide.searchApp.search.menuHeadline")), 1),
                _createElementVNode("div", _hoisted_6, [
                  _createVNode(DepartmentToggle),
                  _createVNode(CloseButton, {
                    class: "right-4 top-2",
                    onClick: onCloseSearch
                  })
                ]),
                _createVNode(SearchForm, { "in-drawer": "" })
              ], 38)
            ], 6)
          ])
        ]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})